/**
 * Member Periods UI Component
 * Displays and manages multiple membership periods for a member
 */

const { useState, useEffect } = React;

export function MemberPeriodsEditor({ memberId, periods = [], onPeriodsChange, onAddPeriod, onUpdatePeriod, onDeletePeriod }) {
  const [editingId, setEditingId] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [isAdding, setIsAdding] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [newPeriod, setNewPeriod] = useState({ start_date: '', end_date: '', notes: '' });

  const formatDateForDisplay = (dateStr) => {
    if (!dateStr) return '(no date set)';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const formatDateForInput = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    // Convert to YYYY-MM-DD format
    return date.toISOString().split('T')[0];
  };

  const handleEdit = (period) => {
    setEditingId(period.id);
    setEditValues({
      start_date: formatDateForInput(period.start_date),
      end_date: formatDateForInput(period.end_date),
      notes: period.notes || ''
    });
  };

  const handleSaveEdit = async (periodId) => {
    try {
      if (!editValues.start_date) {
        alert('Start date is required');
        return;
      }
      // Convert empty strings to null for optional fields
      const updateData = {
        start_date: editValues.start_date,
        end_date: editValues.end_date || null,
        notes: editValues.notes || null
      };
      await onUpdatePeriod(periodId, updateData);
      setEditingId(null);
    } catch (error) {
      console.error('Error saving period:', error);
    }
  };

  const handleAddPeriod = async () => {
    if (!newPeriod.start_date) {
      alert('Start date is required');
      return;
    }
    try {
      // Convert empty strings to null for optional fields
      await onAddPeriod(
        newPeriod.start_date, 
        newPeriod.end_date ? newPeriod.end_date : null, 
        newPeriod.notes ? newPeriod.notes : null
      );
      setNewPeriod({ start_date: '', end_date: '', notes: '' });
      setIsAdding(false);
    } catch (error) {
      console.error('Error adding period:', error);
      alert('Error adding period: ' + error.message);
    }
  };

  const handleDeletePeriod = async (periodId) => {
    if (window.confirm('Delete this membership period?')) {
      try {
        await onDeletePeriod(periodId);
      } catch (error) {
        console.error('Error deleting period:', error);
      }
    }
  };

  return (
    <div style={{ padding: '16px', backgroundColor: 'rgba(255, 255, 255, 0.02)', borderRadius: '8px', border: '1px solid rgba(255, 255, 255, 0.05)' }}>
      <div 
        onClick={() => setIsExpanded(!isExpanded)}
        style={{ marginBottom: isExpanded ? '16px' : '0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', padding: '8px 0', userSelect: 'none' }}
      >
        <h3 style={{ margin: '0', color: 'var(--text-primary)', fontSize: '14px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ display: 'inline-block', transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease' }}>▼</span>
          📅 Membership Periods ({periods.length})
        </h3>
        {!isAdding && isExpanded && (
          <button
            onClick={(e) => { e.stopPropagation(); setIsAdding(true); }}
            style={{
              padding: '6px 12px',
              backgroundColor: 'var(--accent-primary)',
              color: '#fff',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: '600'
            }}
          >
            + Add Period
          </button>
        )}
      </div>

      {isExpanded && (
        <>
          {/* Add new period form */}
          {isAdding && (
            <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: 'rgba(255, 255, 255, 0.05)', borderRadius: '6px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px', marginBottom: '12px' }}>
                <div>
                  <label style={{ fontSize: '11px', color: 'var(--text-muted)', textTransform: 'uppercase', display: 'block', marginBottom: '4px' }}>Start Date *</label>
                  <input
                    type="date"
                    value={newPeriod.start_date}
                    onChange={(e) => setNewPeriod({ ...newPeriod, start_date: e.target.value })}
                    style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid rgba(255, 255, 255, 0.2)', backgroundColor: 'rgba(0, 0, 0, 0.3)', color: 'var(--text-primary)' }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: '11px', color: 'var(--text-muted)', textTransform: 'uppercase', display: 'block', marginBottom: '4px' }}>End Date (optional)</label>
                  <input
                    type="date"
                    value={newPeriod.end_date}
                    onChange={(e) => setNewPeriod({ ...newPeriod, end_date: e.target.value })}
                    style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid rgba(255, 255, 255, 0.2)', backgroundColor: 'rgba(0, 0, 0, 0.3)', color: 'var(--text-primary)' }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: '11px', color: 'var(--text-muted)', textTransform: 'uppercase', display: 'block', marginBottom: '4px' }}>Notes (optional)</label>
                  <input
                    type="text"
                    placeholder="e.g., 'Rejoined'"
                    value={newPeriod.notes}
                    onChange={(e) => setNewPeriod({ ...newPeriod, notes: e.target.value })}
                    style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid rgba(255, 255, 255, 0.2)', backgroundColor: 'rgba(0, 0, 0, 0.3)', color: 'var(--text-primary)' }}
                  />
                </div>
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={handleAddPeriod}
                  style={{ flex: 1, padding: '8px', backgroundColor: 'var(--accent-green)', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: '600' }}
                >
                  ✓ Add
                </button>
                <button
                  onClick={() => setIsAdding(false)}
                  style={{ flex: 1, padding: '8px', backgroundColor: 'rgba(255, 255, 255, 0.1)', color: 'var(--text-primary)', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: '600' }}
                >
                  ✕ Cancel
                </button>
              </div>
            </div>
          )}

          {/* List of periods */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', maxHeight: '500px', overflowY: 'auto', paddingRight: '8px' }}>
            {periods.length === 0 ? (
              <div style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '20px', fontSize: '13px' }}>
                No membership periods recorded
                {!isAdding && (
                  <button
                    onClick={() => setIsAdding(true)}
                    style={{
                      display: 'block',
                      margin: '12px auto 0',
                      padding: '6px 12px',
                      backgroundColor: 'var(--accent-primary)',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '12px',
                      fontWeight: '600'
                    }}
                  >
                    Add First Period
                  </button>
                )}
              </div>
            ) : (
              periods.map((period) => (
            <div key={period.id} style={{ padding: '12px', backgroundColor: 'rgba(255, 255, 255, 0.05)', borderRadius: '6px', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
              {editingId === period.id ? (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: '8px', alignItems: 'end' }}>
                  <div>
                    <label style={{ fontSize: '10px', color: 'var(--text-muted)', display: 'block', marginBottom: '3px' }}>Start Date</label>
                    <input
                      type="date"
                      value={editValues.start_date}
                      onChange={(e) => setEditValues({ ...editValues, start_date: e.target.value })}
                      style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid rgba(255, 255, 255, 0.2)', backgroundColor: 'rgba(0, 0, 0, 0.3)', color: 'var(--text-primary)' }}
                    />
                  </div>
                  <div>
                    <label style={{ fontSize: '10px', color: 'var(--text-muted)', display: 'block', marginBottom: '3px' }}>End Date</label>
                    <input
                      type="date"
                      value={editValues.end_date}
                      onChange={(e) => setEditValues({ ...editValues, end_date: e.target.value })}
                      style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid rgba(255, 255, 255, 0.2)', backgroundColor: 'rgba(0, 0, 0, 0.3)', color: 'var(--text-primary)' }}
                    />
                  </div>
                  <div>
                    <label style={{ fontSize: '10px', color: 'var(--text-muted)', display: 'block', marginBottom: '3px' }}>Notes</label>
                    <input
                      type="text"
                      value={editValues.notes}
                      onChange={(e) => setEditValues({ ...editValues, notes: e.target.value })}
                      style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid rgba(255, 255, 255, 0.2)', backgroundColor: 'rgba(0, 0, 0, 0.3)', color: 'var(--text-primary)' }}
                    />
                  </div>
                  <div style={{ display: 'flex', gap: '4px' }}>
                    <button
                      onClick={() => handleSaveEdit(period.id)}
                      style={{ padding: '6px 10px', backgroundColor: 'var(--accent-green)', color: '#fff', border: 'none', borderRadius: '3px', cursor: 'pointer', fontSize: '12px' }}
                    >
                      ✓
                    </button>
                    <button
                      onClick={() => setEditingId(null)}
                      style={{ padding: '6px 10px', backgroundColor: 'rgba(255, 75, 75, 0.2)', color: 'var(--accent-red)', border: 'none', borderRadius: '3px', cursor: 'pointer', fontSize: '12px' }}
                    >
                      ✕
                    </button>
                  </div>
                </div>
              ) : (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '4px' }}>
                      {formatDateForDisplay(period.start_date)} 
                      {period.end_date ? ` → ${formatDateForDisplay(period.end_date)}` : ' → Present'}
                    </div>
                    {period.notes && (
                      <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                        📝 {period.notes}
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: '4px' }}>
                    <button
                      onClick={() => handleEdit(period)}
                      style={{ padding: '6px 10px', backgroundColor: 'rgba(255, 255, 255, 0.1)', color: 'var(--text-primary)', border: 'none', borderRadius: '3px', cursor: 'pointer', fontSize: '12px' }}
                    >
                      ✏️
                    </button>
                    <button
                      onClick={() => handleDeletePeriod(period.id)}
                      style={{ padding: '6px 10px', backgroundColor: 'rgba(255, 75, 75, 0.2)', color: 'var(--accent-red)', border: 'none', borderRadius: '3px', cursor: 'pointer', fontSize: '12px' }}
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
          </div>
        </>
      )}
    </div>
  );
}
